import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Row } from "react-bootstrap";
import { Tenant } from "./SettingsCustomerPage";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAsterisk } from "@fortawesome/free-solid-svg-icons";

export type CustomerFormData = Omit<Tenant, "Id">;

interface CustomerFormModalProps {
  show: boolean;
  onHide: () => void;
  onFormSubmit: (customerData: CustomerFormData, connectionString: string) => void;
  initialData: Tenant | null;
}

const GUID_PATTERN = "^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$";

const CustomerFormModal = ({
  show,
  onHide,
  onFormSubmit,
  initialData
}: CustomerFormModalProps) => {
  const { t } = useTranslation("parts");
  const isInEditMode = initialData !== null;
  const defaultState = { Guid: "", Name: "", Address: "", Phone: "", RegistrationNo: ""};
  const [formData, setFormData] = useState<CustomerFormData>(defaultState);
  const [connectionString, setConnectionString] = useState<string>("");
  const [validated, setValidated] = useState(false);

  useEffect(() => {
    if (show) {
      setFormData(
        isInEditMode
          ? {
              Guid: initialData!.Guid || "",
              Name: initialData.Name || "",
              Address: initialData.Address || "",
              Phone: initialData.Phone || "",
              RegistrationNo: initialData.RegistrationNo || ""
            }
          : defaultState
      );
      setConnectionString("");
      setValidated(false);
    }
  }, [show, initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const sanitizedValue = value.trim();
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    event.preventDefault();
    event.stopPropagation();
    if (form.checkValidity() === false) {
      setValidated(true);
    } else {
      onFormSubmit(formData, connectionString);
    }
  };

  const getGuidErrorMessage = () => {
    if (formData.Guid === "") {
      return t("settings.forms.provideCustomerGuid");
    }
    return t("settings.forms.invalidGuidFormat");
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isInEditMode ? t("settings.editCustomersInfo") : t("settings.addNewCustomer")}
        </Modal.Title>
      </Modal.Header>
      <Form noValidate validated={validated} onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <h5 className="card-title mb-3">
              {isInEditMode ? t("settings.forms.customer") : t("settings.forms.newCustomer")}
            </h5>
            <Form.Group className="mb-3" controlId="formCustomerGuid">
              <Form.Label>{t("settings.forms.customerGuid")} {<span className="ms-1 text-danger"><FontAwesomeIcon icon={faAsterisk} /></span>}</Form.Label>
              <Form.Control
                type="text"
                name="Guid"
                placeholder={t("settings.forms.enterCustomerGuid")}
                value={formData.Guid}
                onChange={handleChange}
                required
                pattern={GUID_PATTERN}
              />
              <Form.Control.Feedback type="invalid">
                {getGuidErrorMessage()}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="formCustomerName">
              <Form.Label>{t("settings.forms.customerName")} {<span className="ms-1 text-danger"><FontAwesomeIcon icon={faAsterisk} /></span>}</Form.Label>
              <Form.Control
                type="text"
                name="Name"
                placeholder={t("settings.forms.enterCustomerName")}
                value={formData.Name}
                onChange={handleChange}
                required
              />
              <Form.Control.Feedback type="invalid">
                {t("settings.forms.provideCustomerName")}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3" controlId="formCustomerAddress">
              <Form.Label>{t("approvalWorkflows.values.Address")}</Form.Label>
              <Form.Control
                type="text"
                name="Address"
                placeholder={t("settings.forms.enterAddress")}
                value={formData.Address}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formCustomerPhone">
              <Form.Label>{t("approvalWorkflows.values.Phone")}</Form.Label>
              <Form.Control
                type="text"
                name="Phone"
                placeholder={t("settings.forms.enterPhoneNumber")}
                value={formData.Phone}
                onChange={handleChange}
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formCustomerRegistrationNo">
              <Form.Label>{t("settings.tableHeaders.registrationNo")}</Form.Label>
              <Form.Control
                type="text"
                name="RegistrationNo"
                placeholder={t("settings.forms.enterRegistrationNo")}
                value={formData.RegistrationNo}
                onChange={handleChange}
              />
            </Form.Group>
            { !isInEditMode &&            
              <Form.Group className="mb-3" controlId="formTenantConnectionString">
                <Form.Label>{t("settings.forms.connectionString")} {<span className="ms-1 text-danger"><FontAwesomeIcon icon={faAsterisk} /></span>}</Form.Label>
                <Form.Control
                  type="text"
                  name="ConnectionString"
                  placeholder={t("settings.forms.enterConnectionString")} 
                  value={connectionString}
                  onChange={(e) => setConnectionString(e.target.value)}
                  required
                />
                <Form.Control.Feedback type="invalid">
                  {t("settings.forms.provideConnectionString")}
                </Form.Control.Feedback>
              </Form.Group>
            }
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" type="button" onClick={onHide}>
            {t("common:form.buttons.cancel")}
          </Button>
          <Button variant="primary" type="submit">
            {t("common:form.buttons.save")}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CustomerFormModal;
